// src/components/extensions/icons.ts
//
// The icon registry for the Extensions page.
//
// The names live in config/siteConfig.ts as an `ExtensionIcon` union; this map
// turns them into components. Typing it as Record<ExtensionIcon, LucideIcon> is
// the whole point of that split: the two are checked against each other, so a
// new name in the config with no icon here fails the build instead of rendering
// a card with a hole in it.
//
// Keeping icons out of the config also keeps siteConfig free of any UI library
// import, so it stays data that could be moved to JSON.
import { Blocks, FoldVertical, Puzzle, Regex, SquareTerminal, type LucideIcon } from 'lucide-react';

import type { ExtensionIcon } from '../../config/siteConfig';

/**
 * Semantic name -> icon. The name describes what the extension is for, not what
 * the glyph looks like, so an entry can be re-mapped to a better icon later
 * without touching the data.
 */
export const EXTENSION_ICONS: Record<ExtensionIcon, LucideIcon> = {
  /* A fold glyph for anything that collapses code. */
  fold: FoldVertical,
  /* The regular-expression glyph, which is what the extension reads. */
  regex: Regex,
  /* Anything that lives in or drives a terminal. */
  terminal: SquareTerminal,
  /* Anything that inserts or composes blocks of code. */
  blocks: Blocks,
  /* Anything that integrates with other tools. */
  puzzle: Puzzle,
};

/**
 * Used when an entry names no icon. Deliberately the most neutral of the set, so
 * a card with the fallback still looks intentional rather than broken.
 */
export const FALLBACK_ICON = Blocks;

/** Resolves an entry's optional icon name, falling back when it has none. */
export const iconFor = (name: ExtensionIcon | undefined): LucideIcon =>
  (name && EXTENSION_ICONS[name]) || FALLBACK_ICON;
